/**
 * Состояние прогресса LLM-анализа договоров.
 *
 * WS-bridge кладёт сюда события `contract_analysis_progress` —
 * UI читает текущее состояние по contract_id и показывает прогресс-блок
 * пока договор в статусе `under_review`.
 *
 * После события `contract_analyzed` или `contract_deleted` запись для
 * договора удаляется — UI снова показывает обычный список рисков.
 */

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

export type ProgressStage =
  | 'preparing'
  | 'started'
  | 'thinking'
  | 'window_done'
  | 'validating'
  | 'finalizing';

export interface ContractAnalysisProgress {
  contract_id: string;
  version_id: string;
  stage: ProgressStage | string;
  window_current: number | null;
  window_total: number | null;
  risks_so_far: number;
  /** Когда (timestamp ms) пришёл первый прогресс — для расчёта elapsed. */
  started_at: number;
  /** Когда (timestamp ms) пришло последнее обновление. */
  updated_at: number;
}

interface State {
  byContract: Record<string, ContractAnalysisProgress>;
  /**
   * Список contract_id, для которых сейчас идёт фоновая генерация
   * протокола разногласий (Stage G).
   * Plain array т.к. Set не сериализуется в Redux по дефолту.
   */
  generatingProtocols: string[];
}

const initialState: State = {
  byContract: {},
  generatingProtocols: [],
};

const slice = createSlice({
  name: 'analysisProgress',
  initialState,
  reducers: {
    protocolGenerationStarted(state, action: PayloadAction<string>) {
      if (!state.generatingProtocols.includes(action.payload)) {
        state.generatingProtocols.push(action.payload);
      }
    },
    protocolGenerationFinished(state, action: PayloadAction<string>) {
      state.generatingProtocols = state.generatingProtocols.filter(id => id !== action.payload);
    },
    progressReceived(
      state,
      action: PayloadAction<{
        contract_id: string;
        version_id: string;
        stage: string;
        window_current: number | null;
        window_total: number | null;
        risks_so_far: number;
      }>,
    ) {
      const now = Date.now();
      const prev = state.byContract[action.payload.contract_id];
      state.byContract[action.payload.contract_id] = {
        ...action.payload,
        started_at: prev?.started_at ?? now,
        updated_at: now,
      };
    },
    /**
     * Локальный stub: помечаем что юзер запустил анализ. До прихода первого
     * WS-события показываем стадию `preparing` с тикающим таймером, чтобы
     * было видно что бэкенд жив. Реальное событие от сервера потом
     * перезапишет stub (started_at сохраним из stub'а, чтобы таймер не сбросился).
     */
    analysisStarted(
      state,
      action: PayloadAction<{ contract_id: string; version_id?: string }>,
    ) {
      const now = Date.now();
      const prev = state.byContract[action.payload.contract_id];
      // Если уже идёт прогресс — не сбрасываем.
      if (prev && prev.stage !== 'finalizing') return;
      state.byContract[action.payload.contract_id] = {
        contract_id: action.payload.contract_id,
        version_id: action.payload.version_id ?? '',
        stage: 'preparing',
        window_current: null,
        window_total: null,
        risks_so_far: 0,
        started_at: now,
        updated_at: now,
      };
    },
    progressCleared(state, action: PayloadAction<string>) {
      delete state.byContract[action.payload];
    },
  },
});

export const {
  progressReceived,
  progressCleared,
  analysisStarted,
  protocolGenerationStarted,
  protocolGenerationFinished,
} = slice.actions;
export default slice.reducer;
